import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ShopButton from "./ShopButton";
import Header from "./Header";
import cdn from "../utils/cdn";
import FPLink from "./FPLink";

const breakpoints = [425, 576, 650, 760, 892, 1024, 1140]
const mq = breakpoints.map(bp => `@media (min-width: ${bp}px)`)

const rotatingTexts = ["have fun", "get rest", "feel relief", "chill out", "sleep well", "get well", "laugh more", "love more", "live more"];

const Hero = () => {
  const navigate = useNavigate();
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTextIndex((prev) => (prev + 1) % rotatingTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const imageUrl = useMemo(() => (
    cdn.image("/images/shoppage_v2/header_img_01.jpg", { w: 1500 })
  ), []);

  return (
    <>
      <div css={{ ...styles.hero }} >
        <div css={styles.imageContainer}>
          <img src={imageUrl} alt="Full proof" css={styles.mainImage} />
        </div>

        <div css={{ ...styles.textContainer }}>
          <div
            css={{
              padding: "0px 0",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flex: 1,
              justifyContent: "center",
              marginBottom: 50,
            }}
          >
            <h1 css={styles.title}>100% Certified Legal</h1>
            <div css={{ display: "flex", position: "relative" }}>
              <div css={{ ...styles.title_row2, marginTop: "5px" }}>Ways to</div>
              <div css={{ ...styles.underline }}>
                <div css={{ ...styles.title_row2, ...styles.animatedText }}>{rotatingTexts[currentTextIndex]}
                  <sup>*</sup>
                </div>
                <p css={styles.note}>*no med card required</p>
              </div>
            </div>
          </div>

          <div css={{ paddingBottom: 15 }}>
            <ShopButton textStyles={styles.shop.text} buttonStyle={styles.shop.button} theme="light" arrow={true} onClick={() => navigate("/shop")} />
          </div>
        </div>
      </div>

      <div css={styles.categoriesContainer}>
        <p css={styles.categoryTitle}>Shop our trusted selection of</p>
        <div css={{ ...styles.categories }}>
          {["THC", "CBD", "Kava", "Kratom"].map((category) => (
            <FPLink to={"/product-category/" + category} text={category} theme="dark" />
          ))}
        </div>
      </div>

      <div css={{ ...styles.featuresContainer }}>
        {["Curated Quality", "Lab Certified", "Nationwide Shipping"].map((features, index, arr) => (
          <div css={{ display: "flex", alignItems: "center" }} key={features}>
            <span css={{ ...styles.features }}>{features}</span>
            <span>{index < arr.length - 1 && <span css={styles.separator}>|</span>}</span>
          </div>
        ))}
      </div>
    </>
  );
};

const styles = {
  hero: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    color: "white",
    fontFamily: "Instrument Sans",
    fontWeight: 700,
    overflow: "hidden",
    height: 290,
    marginTop: -41,
    minWidth: "100vw",
    backgroundColor: "black",
    zIndex: 0,
    [mq[0]]: {
      height: 350,
    },
    [mq[1]]: {
      height: 400,
    },
    [mq[2]]: {
      height: 450,
    },
    [mq[3]]: {
      height: 250,
    },
    [mq[4]]: {
      height: 290,
      marginTop: -58,
    },
  },
  imageContainer: {
    position: "absolute",
    width: "100%",
    height: "100%",
    zIndex: -1,
    maxWidth: "1120px",
  },
  mainImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center top",
    [mq[6]]: {
      borderBottomRightRadius: "15px",
      borderBottomLeftRadius: "15px"
    },
  },
  navbar: {
    position: "absolute",
    top: 0,
    display: "flex",
    zIndex: 10,
    left: 0,
    right: 0,
  },
  textContainer: {
    zIndex: 5,
    paddingTop: 70,
    paddingBottom: 0,
    width: "90%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  title: {
    fontSize: "1.6rem",
    fontWeight: 700,
    position: "relative",
    fontFamily: "Inter",
    [mq[1]]: {
      fontSize: "2rem",
    },
  },
  title_row2: {
    position: "relative",
    fontFamily: "Inter",
    fontWeight: 700,
    fontSize: "1.6rem",
    [mq[1]]: {
      fontSize: "2rem",
    },
  },
  animatedText: {
    color: "#fff",
    transition: "opacity 0.5s ease-in-out",
    left: "50%",
    transform: "translateX(-50%)",
    position: "absolute",
    bottom: "-4px",
    width: "100%",
    // force single line text
    whiteSpace: "nowrap",
    fontSize: "1.6rem",
    [mq[1]]: {
      fontSize: "2rem",
    },
  },
  underline: {
    marginLeft: 10,
    marginBottom: "2px",
    width: "135px",
    position: "relative",
    display: "flex",
    borderBottom: "solid 2px #fff",
    [mq[1]]: {
      width: "180px",
    },
  },
  note: {
    fontFamily: "Inter",
    fontSize: ".75rem",
    marginTop: "-5px",
    position: "absolute",
    left: 0,
    bottom: -20,
    textAlign: "center",
    width: "135px",
    [mq[1]]: {
      width: "180px",
      fontSize: ".95rem",
    },
  },
  categoriesContainer: {
    width: "100%",
    backgroundColor: "rgba(0,0,0,1)",
    color: "white",
    textAlign: "center",
    padding: "10px 0",
    margin: "0 auto",
    [mq[1]]: {
      padding: "15px 0",
    },
  },
  categoryTitle: {
    fontSize: ".9rem",
    fontWeight: 500,
    [mq[1]]: {
      fontSize: "1rem",
      marginBottom: "5px",
    },
  },
  categories: {
    display: "flex",
    justifyContent: "center",
    padding: "10px 10px 0 10px",
    gap: 5,
    [mq[1]]: {
      gap: 30,
    },
  },
  featuresContainer: {
    display: "flex",
    justifyContent: "center",
    padding: "10px 0",
    backgroundColor: "white",
    color: "black"
  },
  features: {
    fontSize: "1rem",
    fontWeight: 700,
    padding: "0px 5px",
    textAlign: "center",
    [mq[2]]: {
      fontSize: "1.15rem",
      padding: "5px 5px 0",
    },
    [mq[3]]: {
      fontSize: "1.25rem",
      padding: "10px 10px 0",
    },
  },
  shop: {
    button: {
      maxWidth: "380px",
      margin: 0,
    },
    text: {
      fontSize: "1rem",
      lineHeight: "1rem",
      [mq[1]]: {
        fontSize: "1.25rem",
        lineHeight: "1.25rem",
      },
    }
  }
};

export default Hero;
